import { FormControl } from '@mui/material';

import MyDialog from "~/mui/MyDialog";
import { checkForErrorsState, pricePerCreditBE2FE, pricePerCreditFE2BE } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { PENNY_KC_RATIO } from '~/const';
import { apiUpdateProjectVIP } from '~/providers/api';
import { IAdminProject, IAdminUpdateProjectVIP } from "~/providers/admin-interfaces";
import { myUseState } from "~/hooks/myUseState";
import TextFieldFloat from "~/mui/TextFieldFloat";
import { useLang } from "~/hooks/useLang";
import TextFieldNumber from "~/mui/TextFieldNumber";

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
}

// chyby formulare
type TStateProjectErrors = {[key in keyof Pick<IAdminUpdateProjectVIP, "freeCreditsPerMonth" | "pricePerCredit" | "fixedBillingAmount">]: string; };

interface IAdminChangeVIPParamsModal {
	projectData: IAdminProject;
	onSave?: (project: IAdminUpdateProjectVIP) => void;
	onClose?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function AdminChangeVIPParamsModal({
	projectData = null,
	onSave = () => {},
	onClose = () => {},
	onChange = () => {},
}: IAdminChangeVIPParamsModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
	});
	const { state: stateProject, updateState: updateStateProject } = myUseState<IAdminUpdateProjectVIP>({
		fixedBillingAmount: projectData.fixedBillingAmount / PENNY_KC_RATIO,
		freeCreditsPerMonth: projectData.freeCreditsPerMonth,
		pricePerCredit: pricePerCreditBE2FE(projectData.pricePerCredit),
		lastUpdate: projectData.lastUpdate,
	});
	const { state: stateProjectError, setState: setStateProjectError } = myUseState<TStateProjectErrors>({
		fixedBillingAmount: "",
		freeCreditsPerMonth: "",
		pricePerCredit: "",
	});

	function getWasChanged(newState: Partial<IAdminUpdateProjectVIP>): boolean {
		const newStateObj = {
			...stateProject,
			...newState,
		};

		return newStateObj.fixedBillingAmount !== projectData.fixedBillingAmount
			|| newStateObj.freeCreditsPerMonth !== projectData.freeCreditsPerMonth
			|| newStateObj.pricePerCredit !== projectData.pricePerCredit;
	}

	function updateStateProjectWithChange(newState: Partial<IAdminUpdateProjectVIP>) {
		const wasChanged = getWasChanged(newState);

		updateStateProject(newState);
		updateState({
			wasChanged,
		});
		onChange(wasChanged);
	}

	async function dialogOnSave() {
		// validTill - nemuze byt prazdne
		const errorData = checkForErrorsState<IAdminUpdateProjectVIP, TStateProjectErrors>(stateProject, {
			fixedBillingAmount: { check: "number-eg0", msg: trans("createProject.error.fixedBillingAmount") },
			freeCreditsPerMonth: { check: "number-bigint", msg: trans("biginit.error") },
			pricePerCredit: { check: "number-eg0", msg: trans("createProject.error.pricePerCredit") },
		});

		setStateProjectError(errorData.errorsState);

		// jsou tam chyby
		if (errorData.errorsCount > 0) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const updateData = await apiUpdateProjectVIP(projectData.id, {
			fixedBillingAmount: stateProject.fixedBillingAmount * PENNY_KC_RATIO,
			freeCreditsPerMonth: stateProject.freeCreditsPerMonth,
			pricePerCredit: pricePerCreditFE2BE(stateProject.pricePerCredit),
			lastUpdate: stateProject.lastUpdate,
		}, lang);

		if (updateData.noErrors) {
			onSave(updateData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: updateData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("adminChangeVIPParamsModal.title")} onClose={onClose} onSave={dialogOnSave} className="adminChangeVIPParamsModal" dialogLoading={state.dialogLoading} disableSave={!state.wasChanged}
		errorMsg={state.errorMsg} saveCaption={trans("saveChanges")} confirmClose={state.wasChanged} minWidth="450px">
		<FormControl sx={{ width: "100%" }}>
			<TextFieldFloat
				label={trans("createProject.pricePerCredit")}
				value={stateProject.pricePerCredit}
				onChange={value => updateStateProjectWithChange({ pricePerCredit: value })}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateProjectError.pricePerCredit !== ""}
				helperText={stateProjectError.pricePerCredit}
			/>
			<TextFieldNumber
				label={trans("createProject.fixedBillingAmount")}
				value={stateProject.fixedBillingAmount}
				onChange={fixedBillingAmount => updateStateProjectWithChange({ fixedBillingAmount })}
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateProjectError.fixedBillingAmount !== ""}
				helperText={stateProjectError.fixedBillingAmount}
			/>
			<TextFieldNumber
				label={trans("createProject.freeCreditsPerMonth")}
				value={stateProject.freeCreditsPerMonth}
				onChange={freeCreditsPerMonth => updateStateProjectWithChange({ freeCreditsPerMonth })}
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateProjectError.freeCreditsPerMonth !== ""}
				helperText={stateProjectError.freeCreditsPerMonth}
			/>
		</FormControl>
	</MyDialog>;
}
